import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../../shared/components/Icon';
import FilterPopup from '../../../../shared/components/modals/FilterPopup';
import { useOverviewFilters } from '../../../../shared/context/OverviewFilterContext';
import axiosInstance from '../../../../shared/services/api/axiosInstance';
import ProductUsageMetricsCards from './components/ProductUsageMetricsCards';
import TopPagePathsChart from './components/TopPagePathsChart';
import UserCorrelationMatrix from './components/UserCorrelationMatrix';
import { getDefaultDateRange } from '../../../../shared/utils/dateUtils';

const ProductUsage = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { appliedFilters, updateFilters, hasActiveFilters, teamId } = useOverviewFilters();
  const [metrics, setMetrics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasInitializedFilters, setHasInitializedFilters] = useState(false);

  // Initialize default filters (first website and first contract) when ProductUsage mounts
  useEffect(() => {
    const initializeDefaultFilters = async () => {
      if (!teamId || hasInitializedFilters) return;
      
      // Only initialize if no filters are currently set
      const hasWebsites = appliedFilters.websites && appliedFilters.websites.length > 0;
      const hasContracts = appliedFilters.contracts && appliedFilters.contracts.length > 0;
      
      if (hasWebsites || hasContracts) {
        setHasInitializedFilters(true);
        return;
      }

      try {
        // Fetch websites
        let firstWebsiteId = null;
        try {
          const isNumericId = typeof teamId === 'number' || (!isNaN(teamId) && Number.isInteger(Number(teamId)));
          const websitesResponse = isNumericId
            ? await axiosInstance.get(`/website/team/${teamId}`)
            : await axiosInstance.get(`/website/name/${encodeURIComponent(teamId)}`);
          
          if (websitesResponse.data?.websites?.length > 0) {
            const activeWebsites = websitesResponse.data.websites.filter(w => w.is_verified || w.isVerified);
            if (activeWebsites.length > 0) {
              firstWebsiteId = activeWebsites[0].site_id || activeWebsites[0].siteId;
            }
          }
        } catch (err) {
          console.warn('Error fetching websites for default filter:', err);
        }

        // Fetch contracts
        let firstContract = null;
        try {
          let teamName = teamId;
          if (typeof teamId === 'number' || !isNaN(teamId)) {
            teamName = localStorage.getItem('selectedTeam') || teamId;
          }
          
          const contractsResponse = await axiosInstance.get(`/smart-contracts/team/${encodeURIComponent(teamName)}`);
          
          if (contractsResponse.data?.contracts?.length > 0) {
            const contract = contractsResponse.data.contracts[0];
            firstContract = {
              id: contract.contractId || contract.id,
              address: contract.address || contract.contract_address,
              name: contract.name || contract.contract_name || `Contract ${(contract.address || contract.contract_address || '').substring(0, 6)}...${(contract.address || contract.contract_address || '').substring((contract.address || contract.contract_address || '').length - 4)}`,
              blockchain: contract.blockchain || contract.chain,
              tokenSymbol: contract.tokenSymbol || contract.symbol || contract.contract_symbol,
              contractType: contract.contractType || 'main'
            };
          }
        } catch (err) {
          console.warn('Error fetching contracts for default filter:', err);
        }

        // Update filters with first website and first contract
        if (firstWebsiteId || firstContract) {
          updateFilters({
            websites: firstWebsiteId ? [firstWebsiteId] : [],
            contracts: firstContract ? [firstContract] : [],
            sessions: appliedFilters.sessions || [],
            userIdentities: appliedFilters.userIdentities || [],
            dateRange: appliedFilters.dateRange || getDefaultDateRange()
          });
        }
        setHasInitializedFilters(true);
      } catch (error) {
        console.error('Error initializing default filters:', error);
        setHasInitializedFilters(true); // Set to true even on error to prevent infinite loops
      }
    };

    initializeDefaultFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, hasInitializedFilters]);

  // Reset initialization flag when team changes
  useEffect(() => {
    setHasInitializedFilters(false);
  }, [teamId]);

  const handleFiltersChange = (filters) => {
    updateFilters(filters);
  };

  // Fetch product usage metrics
  const fetchProductUsageMetrics = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (!teamId) {
        setMetrics({
          totalPageViews: 0,
          avgPageViewsPerUser: 0,
          avgDurationPerPage: 0
        });
        return;
      }

      const params = { team_id: teamId };

      // Add website filter if selected
      if (appliedFilters.websites && appliedFilters.websites.length > 0) {
        params.websites = appliedFilters.websites.join(',');
      }

      // Add date range if specified
      if (appliedFilters.dateRange?.start) {
        params.from = appliedFilters.dateRange.start;
        params.to = appliedFilters.dateRange.end;
      }

      const response = await axiosInstance.get('/overview/product-usage-metrics', { params });

      if (response.data?.success) {
        setMetrics(response.data.metrics);
      } else {
        console.warn('Product usage metrics response not successful:', response.data);
        setMetrics({
          totalPageViews: 0,
          avgPageViewsPerUser: 0,
          avgDurationPerPage: 0
        });
      }
    } catch (error) {
      console.error('Error fetching product usage metrics:', error);
      setError(error.message);
      setMetrics({
        totalPageViews: 0,
        avgPageViewsPerUser: 0,
        avgDurationPerPage: 0
      });
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters, teamId]);

  useEffect(() => {
    fetchProductUsageMetrics();
  }, [fetchProductUsageMetrics]);

  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  return (
    <div className="relative h-full w-full overflow-y-auto">
      {/* Filter Button - Vertical, fixed to right edge */}
      <button
        onClick={() => setIsFilterOpen(true)}
        className="fixed z-10 transition-colors shadow-sm flex items-center justify-center gap-2 bg-white/80 backdrop-blur-sm hover:bg-white text-[#475569]"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          right: 0,
          top: '16px',
          padding: '16px 8px',
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          transform: 'none',
          border: '1px solid #DDE2EE',
          borderRight: 'none',
          borderRadius: 0
        }}
      >
        <Icon name="filter" size={18} style={{ transform: 'rotate(90deg)' }} />
        <span className="text-sm font-medium">Filters</span>
        {hasActiveFilters() && (
          <span className="text-xs opacity-90">•</span>
        )}
      </button>

      {/* Page Content */}
      <div className="p-4 md:p-6 lg:p-8 pr-20">
        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded">
            Error: {error}
          </div>
        )}

        {/* Metrics Cards */}
        <ProductUsageMetricsCards data={metrics} isLoading={isLoading} />

        {/* Top Page Paths Chart */}
        <div className="mt-4">
          <TopPagePathsChart 
            teamId={teamId} 
            appliedFilters={appliedFilters}
            isLoading={isLoading}
          />
        </div>

        {/* User Correlation Matrix */}
        <div className="mt-4">
          <UserCorrelationMatrix 
            teamId={teamId} 
            appliedFilters={appliedFilters}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Filter Popup */}
      <FilterPopup 
        isOpen={isFilterOpen} 
        onClose={() => setIsFilterOpen(false)}
        onFiltersChange={handleFiltersChange}
        appliedFilters={appliedFilters}
        teamId={localStorage.getItem('selectedTeam') || 'Cryptique'}
      />
    </div>
  );
};

export default ProductUsage;

